/** Explicit user-selected Web Serial ports. Importing never discovers devices. */
export class HardwareError extends Error {
  constructor(message) { super(message); this.name = 'HardwareError'; }
}
export class ProtocolError extends HardwareError {
  constructor(message) { super(message); this.name = 'ProtocolError'; }
}
export class HardwareTimeout extends HardwareError {
  constructor(message) { super(message); this.name = 'HardwareTimeout'; }
}
export class UnsupportedCapability extends HardwareError {
  constructor(message) { super(message); this.name = 'UnsupportedCapability'; }
}
export class OperationCancelled extends HardwareError {
  constructor(message = 'Instrument operation cancelled.') { super(message); this.name = 'AbortError'; }
}

export const SERIAL_OPTIONS = Object.freeze(Object.fromEntries(Object.entries({
  meter: { baudRate: 38400, dataBits: 7, stopBits: 2, parity: 'even', flowControl: 'hardware', bufferSize: 4096 },
  generator: { baudRate: 115200, dataBits: 8, stopBits: 1, parity: 'none', flowControl: 'none', bufferSize: 4096 },
  monitor: { baudRate: 9600, dataBits: 8, stopBits: 1, parity: 'none', flowControl: 'none', bufferSize: 4096 },
}).map(([key, value]) => [key, Object.freeze(value)])));

const owners = new WeakMap();
const padding = value => value === 13 || value === 10;
const milliseconds = (value, maximum = 120000) => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 50 || value > maximum) throw new HardwareError('Instrument timeout is outside 50..120000 milliseconds.');
  return value;
};

export class WebSerialTransport {
  constructor(port, settings, { signal, timeoutMs = 3000, openTimeoutMs = 10000, closeTimeoutMs = 1000,
    clock = { setTimeout: (...args) => setTimeout(...args), clearTimeout: timer => clearTimeout(timer) } } = {}) {
    if (!port || typeof port.open !== 'function' || typeof port.close !== 'function') throw new HardwareError('Select an explicit browser serial port.');
    if (!settings || ![9600, 38400, 115200].includes(settings.baudRate) || ![7, 8].includes(settings.dataBits)
      || ![1, 2].includes(settings.stopBits) || !['none', 'even'].includes(settings.parity) || !['none', 'hardware'].includes(settings.flowControl)) {
      throw new UnsupportedCapability('Serial format is outside the reviewed station interfaces.');
    }
    this.port = port; this.settings = { ...settings, bufferSize: 4096 }; this.signal = signal;
    this.timeoutMs = milliseconds(timeoutMs); this.openTimeoutMs = milliseconds(openTimeoutMs); this.closeTimeoutMs = milliseconds(closeTimeoutMs, 10000);
    this.clock = clock; this.state = 'idle'; this.failed = false; this.reader = null; this.writer = null;
    this.active = null; this.opened = false; this.opening = null; this.cleanup = null;
    this.abortListener = () => this.fail(new OperationCancelled());
    this.disconnectListener = () => this.fail(new HardwareError('The selected serial instrument disconnected.'));
  }

  bounded(promise, duration, message, signal) {
    return new Promise((resolve, reject) => {
      let timer;
      const remove = () => { this.clock.clearTimeout(timer); signal?.removeEventListener('abort', abort); };
      const abort = () => { remove(); reject(new OperationCancelled()); };
      if (signal?.aborted) { reject(new OperationCancelled()); return; }
      timer = this.clock.setTimeout(() => { remove(); reject(new HardwareTimeout(message)); }, duration);
      signal?.addEventListener('abort', abort, { once: true });
      Promise.resolve(promise).then(value => { remove(); resolve(value); }, error => { remove(); reject(error); });
    });
  }

  async connect() {
    if (this.signal?.aborted) throw new OperationCancelled();
    if (!['idle', 'closed'].includes(this.state) || owners.has(this.port)) throw new HardwareError('This serial port is open, failed or still closing; reconnect after cleanup.');
    this.state = 'opening'; this.failed = false; this.cleanup = null; this.opened = false;
    owners.set(this.port, this);
    this.signal?.addEventListener('abort', this.abortListener, { once: true });
    this.port.addEventListener?.('disconnect', this.disconnectListener);
    this.opening = Promise.resolve().then(() => {
      if (this.failed || this.signal?.aborted || this.state !== 'opening') throw new OperationCancelled();
      return this.port.open(this.settings);
    }).then(() => { this.opened = true; });
    try {
      await this.bounded(this.opening, this.openTimeoutMs, 'Serial port did not open before its deadline.', this.signal);
      if (this.failed || this.signal?.aborted || this.state !== 'opening') throw new OperationCancelled();
      if (!this.port.readable || !this.port.writable) throw new HardwareError('Serial port streams are unavailable.');
      this.reader = this.port.readable.getReader(); this.writer = this.port.writable.getWriter();
      this.state = 'open';
      this.pumpTask = this.pump();
      await Promise.resolve();
      if (this.failed) throw this.failure;
      return this;
    } catch (error) {
      const safe = error instanceof HardwareError ? error : new HardwareError('Could not open the selected serial instrument.');
      this.fail(safe); throw safe;
    }
  }

  async pump() {
    const reader = this.reader;
    try {
      while (this.state === 'open' && !this.failed) {
        const { value, done } = await reader.read();
        if (done) { if (this.state === 'open' && !this.failed) this.fail(new HardwareError('Serial input ended before the instrument was closed.')); break; }
        if (!(value instanceof Uint8Array)) throw new ProtocolError('Serial input is not a byte sequence.');
        this.receive(value);
      }
    } catch (error) {
      if (this.state === 'open' && !this.failed) this.fail(error instanceof HardwareError ? error : new HardwareError('Serial input failed; reconnect before continuing.'));
    } finally {
      try { reader.releaseLock(); } catch { /* Cleanup also attempts lock release. */ }
    }
  }

  receive(chunk) {
    const current = this.active;
    if (!current) {
      if (chunk.some(value => !padding(value))) this.fail(new ProtocolError('Unsolicited or stale serial input; reconnect before continuing.'));
      return;
    }
    try {
      for (const value of chunk) {
        if (current.reply) {
          if (!padding(value)) throw new ProtocolError('Unexpected bytes followed the complete instrument reply.');
          continue;
        }
        if (!current.scom && current.bytes.length === 0 && padding(value)) continue;
        current.bytes.push(value);
        if (current.bytes.length > current.maxBytes) throw new ProtocolError('Serial reply exceeded its bounded size.');
        if (current.scom && current.bytes.length === 6) current.expectedLength = 7 + current.bytes[5] + (current.bytes[5] ? 1 : 0);
        if (current.expectedLength > current.maxBytes) throw new ProtocolError('SCOM reply declares an oversized payload.');
        if ((current.scom && current.bytes.length === current.expectedLength) || (!current.scom && value === current.terminator)) {
          current.reply = Uint8Array.from(current.bytes);
        }
      }
      this.complete();
    } catch (error) { this.fail(error); }
  }

  complete() {
    const current = this.active;
    if (current?.writeComplete && current.reply) {
      this.active = null; this.clock.clearTimeout(current.timer); current.resolve(current.reply);
    }
  }

  async exchange(request, { terminator = 13, scom = false, timeoutMs = this.timeoutMs, maxBytes = 4096 } = {}) {
    milliseconds(timeoutMs);
    if (!(request instanceof Uint8Array) || request.length < 1 || request.length > 4096 || typeof scom !== 'boolean' || !Number.isInteger(maxBytes) || maxBytes < 7 || maxBytes > 4096) throw new HardwareError('Serial request or bounded reply size is invalid.');
    if (!scom && ![13, 59].includes(terminator)) throw new UnsupportedCapability('Unqualified serial reply delimiter.');
    if (this.signal?.aborted) throw new OperationCancelled();
    if (this.state !== 'open' || this.failed) throw new HardwareError('Serial connection is closed or requires reconnection after an error.');
    if (this.active) throw new HardwareError('Only one instrument exchange may be active on a port.');
    return new Promise((resolve, reject) => {
      const current = { resolve, reject, terminator, scom, maxBytes, bytes: [], expectedLength: null, reply: null, writeComplete: false };
      this.active = current;
      current.timer = this.clock.setTimeout(() => this.fail(new HardwareTimeout('No complete instrument reply before the serial deadline; reconnect before retrying.')), timeoutMs);
      Promise.resolve().then(() => {
        if (this.active !== current || this.failed || this.signal?.aborted) throw new OperationCancelled();
        return this.writer.write(request.slice());
      }).then(() => {
        if (this.active === current) { current.writeComplete = true; this.complete(); }
      }, () => { if (this.active === current) this.fail(new HardwareError('Serial write failed; reconnect before continuing.')); });
    });
  }

  fail(error) {
    if (this.state === 'closed' || this.failed) return;
    this.failed = true; this.failure = error;
    if (this.active) {
      const current = this.active; this.active = null; this.clock.clearTimeout(current.timer); current.reject(error);
    }
    this.startCleanup();
  }

  startCleanup() {
    if (this.cleanup) return this.cleanup;
    this.state = 'closing';
    this.signal?.removeEventListener('abort', this.abortListener);
    this.port.removeEventListener?.('disconnect', this.disconnectListener);
    this.cleanup = (async () => {
      await this.opening?.catch(() => {});
      const reader = this.reader, writer = this.writer;
      const cancel = (target, method) => {
        try { return Promise.resolve(target?.[method]?.()).catch(() => {}); } catch { return Promise.resolve(); }
      };
      await Promise.all([cancel(reader, 'cancel'), cancel(writer, 'abort')]);
      try { reader?.releaseLock(); } catch { /* A read may have already released it. */ }
      try { writer?.releaseLock(); } catch { /* Keep port ownership until native close succeeds. */ }
      if (this.opened) await this.port.close();
      this.reader = null; this.writer = null; this.state = 'closed'; this.opened = false;
      if (owners.get(this.port) === this) owners.delete(this.port);
    })();
    this.cleanup.catch(() => {});
    return this.cleanup;
  }

  async close() {
    if (this.active) this.fail(new HardwareError('Instrument connection was closed during an exchange.'));
    await this.bounded(this.startCleanup(), this.closeTimeoutMs, 'Serial cleanup is still pending; this port cannot be reused yet.');
  }
}
